import React, { useState } from "react";
import { Lock } from "lucide-react";
import BrandStripe from "@/components/BrandStripe";
import Logo from "@/components/Logo";
import { Divider } from "@/components/Divider";

const PIN = "7427";
const STORAGE_KEY = "dwig_gate_unlocked";

export default function ComingSoon({ onUnlock }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (pin === PIN) {
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
      onUnlock?.();
    } else {
      setError("Incorrect PIN. Please try again.");
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="page-coming-soon">
      <BrandStripe thickness="h-1.5" />
      <main className="flex-1 grid lg:grid-cols-2">
        {/* LEFT — message */}
        <section className="flex items-center justify-center px-6 sm:px-10 lg:px-16 py-16 relative overflow-hidden">
          <div className="absolute inset-0 hero-grid opacity-50" />
          <div className="relative w-full max-w-xl">
            <Logo size={52} />
            <p className="mt-10 text-xs uppercase tracking-[0.35em] font-bold text-[#00A651]">
              Coming Soon
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-black mt-3 tracking-tight leading-[1.05]">
              Something <span className="text-[#00A651]">remarkable</span>
              <br />
              is on the way.
            </h1>
            <Divider className="my-6" />
            <p className="font-sub italic text-lg sm:text-xl text-gray-700">
              DW Investment Group&apos;s new home for mortgages, credit repair, and
              student loan repair is almost ready. Returning clients can enter the
              access PIN to preview the site.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { l: "Launching", v: "Soon", c: "#00A651" },
                { l: "Programs", v: "6+", c: "#E88C2F" },
                { l: "NMLS", v: "Verified", c: "#ED1C24" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 pl-3" style={{ borderColor: s.c }}>
                  <div className="font-heading font-bold text-lg text-black">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* RIGHT — gate */}
        <section className="bg-black text-white flex items-center justify-center px-6 sm:px-10 lg:px-16 py-16 relative">
          <div className="absolute top-0 left-0 right-0 h-1 flex">
            <span className="flex-1 bg-[#00A651]" />
            <span className="flex-1 bg-[#E88C2F]" />
            <span className="flex-1 bg-[#ED1C24]" />
            <span className="flex-1 bg-[#F26522]" />
          </div>
          <form
            onSubmit={submit}
            className={`w-full max-w-md ${shake ? "animate-[wiggle_0.4s_ease-in-out]" : ""}`}
            data-testid="coming-soon-form"
          >
            <div className="w-12 h-12 grid place-items-center bg-[#00A651]/15 text-[#00A651] rounded-sm">
              <Lock size={20} />
            </div>
            <p className="mt-6 text-xs uppercase tracking-[0.3em] font-bold text-[#E88C2F]">
              Client Access
            </p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">
              Enter access PIN
            </h2>
            <p className="font-sub italic text-gray-400 mt-2">
              4-digit code provided by your loan originator.
            </p>

            <label className="sr-only" htmlFor="pin-input">Access PIN</label>
            <input
              id="pin-input"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={8}
              value={pin}
              onChange={(e) => { setPin(e.target.value); setError(""); }}
              data-testid="coming-soon-pin-input"
              placeholder="• • • •"
              className="mt-8 w-full bg-transparent border-b-2 border-gray-700 focus:border-[#00A651] outline-none font-heading font-bold text-3xl tracking-[0.5em] py-3 text-center transition-colors"
              aria-invalid={!!error}
            />
            {error && (
              <p
                role="alert"
                data-testid="coming-soon-error"
                className="mt-3 text-sm text-[#ED1C24] font-bold uppercase tracking-wider"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="coming-soon-submit"
              className="mt-8 w-full bg-[#00A651] hover:bg-[#008f45] text-white font-bold uppercase tracking-wider text-sm px-6 py-4 rounded-sm transition-colors"
            >
              Unlock Site
            </button>

            <div className="mt-10 pt-6 border-t border-gray-800 text-xs text-gray-500 flex items-center justify-between">
              <span>© {new Date().getFullYear()} DW Investment Group</span>
              <span className="font-sub italic">Insuring Your Future…Today</span>
            </div>
          </form>
        </section>
      </main>
      <BrandStripe thickness="h-1.5" />

      <style>{`
        @keyframes wiggle {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          75% { transform: translateX(8px); }
        }
      `}</style>
    </div>
  );
}

export const isGateUnlocked = () => {
  try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
};

export const lockGate = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
};
